import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GRC_INDUSTRY_PACK_KEYS } from '../grc/grc-form.models';
import { PlatformAdminService, TenantRecord } from './platform-admin.service';

@Component({
  standalone: true,
  selector: 'ain-platform-tenants',
  providers: [ConfirmationService],
  imports: [AinPageComponent, AinStateComponent, AinSearchAccordionComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule, InputTextModule, DialogModule, ConfirmDialogModule, RouterLink, SelectModule],
  template: `
    <ain-page [eyebrow]="t('platform.eyebrow')" [title]="t('platform.tenants.title')" [description]="t('platform.tenants.description')">
      <div page-actions>
        <ain-info-hint
          titleKey="platform.tenants.hint.title"
          descriptionKey="platform.tenants.hint.description"
          [bulletKeys]="['platform.tenants.hint.bullet1', 'platform.tenants.hint.bullet2', 'platform.tenants.hint.bullet3', 'platform.tenants.hint.bullet4']" />
        <a pButton severity="secondary" [routerLink]="['/platform/tenants/provision']" [label]="t('platform.tenants.provision.action')"></a>
        <button pButton type="button" [label]="t('platform.tenants.create')" (click)="openCreateDialog()"></button>
      </div>

      <p-confirmDialog />

      <ain-search-accordion [title]="t('platform.search.title')">
        <section class="search-form">
          <input pInputText [(ngModel)]="filters.query" [placeholder]="t('platform.search.query')" />
          <input pInputText [(ngModel)]="filters.slug" [placeholder]="t('platform.tenants.slug')" />
          <select [(ngModel)]="filters.status">
            <option value="">{{ t('platform.search.anyStatus') }}</option>
            <option value="0">{{ t('platform.tenants.status.draft') }}</option>
            <option value="1">{{ t('platform.tenants.status.active') }}</option>
            <option value="2">{{ t('platform.tenants.status.suspended') }}</option>
            <option value="3">{{ t('platform.tenants.status.deleted') }}</option>
          </select>
          <button pButton type="button" [label]="t('platform.search.apply')" (click)="reload()"></button>
          <button pButton type="button" severity="secondary" [label]="t('platform.search.clear')" (click)="clearFilters()"></button>
        </section>
      </ain-search-accordion>

      @if (loading()) {
        <ain-state type="loading" [title]="t('platform.tenants.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('platform.tenants.unavailable')" [message]="error() ?? ''" />
      } @else {
        <p-table [value]="filteredTenants()">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('columns.name') }}</th>
              <th>{{ t('platform.tenants.slug') }}</th>
              <th>{{ t('platform.tenants.legalName') }}</th>
              <th>{{ t('columns.status') }}</th>
              <th>{{ t('common.open') }}</th>
              <th>{{ t('common.delete') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.name }}</td>
              <td>{{ row.slug }}</td>
              <td>{{ row.legalName }}</td>
              <td>{{ tenantStatus(row.status) }}</td>
              <td><a [routerLink]="['/platform/tenants', row.id]">{{ t('common.open') }}</a></td>
              <td>
                @if (isDraft(row.status)) {
                  <button
                    pButton
                    type="button"
                    severity="danger"
                    [label]="t('platform.tenants.provision.discardDraft')"
                    [disabled]="saving()"
                    (click)="confirmDiscard(row)"></button>
                }
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6">{{ t('common.noRecords') }}</td>
            </tr>
          </ng-template>
        </p-table>
      }

      <p-dialog [header]="t('platform.tenants.create')" [visible]="createDialogVisible()" [modal]="true" [style]="{ width: '52rem', maxWidth: '95vw' }" (visibleChange)="createDialogVisible.set($event)">
        <ain-info-hint
          titleKey="platform.tenants.hint.title"
          descriptionKey="platform.tenants.hint.description"
          [bulletKeys]="['platform.tenants.hint.bullet1', 'platform.tenants.hint.bullet2', 'platform.tenants.hint.bullet3', 'platform.tenants.hint.bullet4']" />
        <section class="create-form">
          <input pInputText [(ngModel)]="createModel.name" [placeholder]="t('platform.tenants.name')" />
          <input pInputText [(ngModel)]="createModel.slug" [placeholder]="t('platform.tenants.slug')" />
          <input pInputText [(ngModel)]="createModel.legalName" [placeholder]="t('platform.tenants.legalName')" />
          <input pInputText [(ngModel)]="createModel.host" [placeholder]="t('platform.tenants.host')" />
          <input pInputText [(ngModel)]="createModel.storeName" [placeholder]="t('platform.tenants.storeName')" />
          <input pInputText [(ngModel)]="createModel.defaultCulture" [placeholder]="t('platform.tenants.defaultCulture')" />
          <input pInputText [(ngModel)]="createModel.defaultTimeZone" [placeholder]="t('platform.tenants.defaultTimeZone')" />
          <p-select
            [options]="industryPackOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="createModel.industryPackKey"
            appendTo="body"
            [placeholder]="t('platform.tenants.industryPack')" />
          <button pButton type="button" [label]="t('common.save')" (click)="createTenant()" [disabled]="saving()"></button>
        </section>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .search-form { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; align-items: center; }
    .search-form select { border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px; min-height: 40px; }
    .create-form { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
    @media (max-width: 1200px) { .create-form, .search-form { grid-template-columns: 1fr; } }
    a { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
  `]
})
export class PlatformTenantsComponent implements OnInit {
  private readonly api = inject(PlatformAdminService);
  private readonly localizationService = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);

  readonly tenants = signal<TenantRecord[]>([]);
  readonly filteredTenants = computed(() => {
    const query = this.filters.query.trim().toLowerCase();
    const slug = this.filters.slug.trim().toLowerCase();
    const status = this.filters.status.trim();

    return this.tenants().filter(row => {
      const matchesQuery = !query ||
        row.name.toLowerCase().includes(query) ||
        row.legalName.toLowerCase().includes(query);
      const matchesSlug = !slug || row.slug.toLowerCase().includes(slug);
      const matchesStatus = !status || `${row.status}` === status;
      return matchesQuery && matchesSlug && matchesStatus;
    });
  });
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly createDialogVisible = signal(false);
  filters = {
    query: '',
    slug: '',
    status: ''
  };

  createModel = {
    name: '',
    slug: '',
    legalName: '',
    host: '',
    storeName: '',
    defaultCulture: 'fa-IR',
    defaultTimeZone: 'Asia/Tehran',
    industryPackKey: 'bank'
  };

  industryPackOptions(): Array<{ label: string; value: string }> {
    return GRC_INDUSTRY_PACK_KEYS.map(value => ({
      value,
      label: this.t(`grc.foundation.settings.industryPack.${value}`)
    }));
  }

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listTenants({
      search: this.filters.query.trim(),
      slug: this.filters.slug.trim(),
      status: this.filters.status.trim() || undefined,
      skip: 0,
      take: 200
    }).subscribe({
      next: rows => {
        this.tenants.set(rows.items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('platform.tenants.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  createTenant(): void {
    const payload = {
      name: this.createModel.name.trim(),
      slug: this.createModel.slug.trim(),
      legalName: this.createModel.legalName.trim(),
      host: this.createModel.host.trim(),
      storeName: this.createModel.storeName.trim() || undefined,
      defaultCulture: this.createModel.defaultCulture.trim(),
      defaultTimeZone: this.createModel.defaultTimeZone.trim()
    };

    if (!payload.name || !payload.slug || !payload.legalName || !payload.defaultCulture || !payload.defaultTimeZone) {
      return;
    }

    this.saving.set(true);
    if (payload.host) {
      this.api.provisionCustomerSite({
        name: payload.name,
        slug: payload.slug,
        legalName: payload.legalName,
        host: payload.host,
        storeName: payload.storeName,
        defaultCulture: payload.defaultCulture,
        defaultTimeZone: payload.defaultTimeZone,
        industryPackKey: this.createModel.industryPackKey
      }).subscribe({
        next: created => this.finishCreate(created.tenant),
        error: (err: HttpErrorResponse) => {
          this.error.set(this.provisionErrorMessage(err));
          this.saving.set(false);
        }
      });
      return;
    }

    this.api.createTenant({
      name: payload.name,
      slug: payload.slug,
      legalName: payload.legalName,
      defaultCulture: payload.defaultCulture,
      defaultTimeZone: payload.defaultTimeZone,
      industryPackKey: this.createModel.industryPackKey
    }).subscribe({
      next: created => this.finishCreate(created),
      error: () => {
        this.error.set(this.t('platform.tenants.errorCreate'));
        this.saving.set(false);
      }
    });
  }

  private finishCreate(tenant: TenantRecord): void {
    this.tenants.update(rows => [tenant, ...rows]);
    this.createModel.name = '';
    this.createModel.slug = '';
    this.createModel.legalName = '';
    this.createModel.host = '';
    this.createModel.storeName = '';
    this.createModel.industryPackKey = 'bank';
    this.createDialogVisible.set(false);
    this.saving.set(false);
  }

  private provisionErrorMessage(err: HttpErrorResponse): string {
    const code = String((err.error as { Error?: string } | null)?.Error ?? '').trim();
    if (code === 'host_already_bound') {
      return this.t('platform.tenants.errorHostAlreadyBound');
    }
    if (code === 'tenant_slug_exists') {
      return this.t('platform.tenants.errorSlugExists');
    }
    if (code === 'tenant_not_draft') {
      return this.t('platform.tenants.provision.errorNotDraft');
    }
    if (code === 'bootstrap_tenant_protected') {
      return this.t('platform.tenants.provision.errorBootstrapProtected');
    }
    if (code === 'host_required') {
      return this.t('platform.tenants.errorHostRequired');
    }
    if (code === 'invalid_industry_pack') {
      return this.t('platform.tenants.errorInvalidIndustryPack');
    }
    return this.t('platform.tenants.errorProvision');
  }

  openCreateDialog(): void {
    this.createDialogVisible.set(true);
  }

  clearFilters(): void {
    this.filters.query = '';
    this.filters.slug = '';
    this.filters.status = '';
    this.reload();
  }

  tenantStatus(value: number | string): string {
    if (typeof value === 'string') {
      return value;
    }

    const map: Record<number, string> = {
      0: this.t('platform.tenants.status.draft'),
      1: this.t('platform.tenants.status.active'),
      2: this.t('platform.tenants.status.suspended'),
      3: this.t('platform.tenants.status.deleted')
    };

    return map[value] ?? `${value}`;
  }

  isDraft(status: number | string): boolean {
    return status === 0 || String(status).toLowerCase() === 'draft';
  }

  confirmDiscard(row: TenantRecord): void {
    this.confirmation.confirm({
      header: this.t('platform.tenants.provision.discardDraft'),
      message: this.t('platform.tenants.provision.discardDraftConfirm'),
      accept: () => this.discardDraft(row.id)
    });
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  private discardDraft(tenantId: string): void {
    this.saving.set(true);
    this.api.discardDraftTenant(tenantId).subscribe({
      next: () => {
        this.tenants.update(rows => rows.filter(item => item.id !== tenantId));
        this.saving.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.error.set(this.provisionErrorMessage(err));
        this.saving.set(false);
      }
    });
  }
}
